/**
 * ExemplarGallery — grid of exemplar creatives with live image thumbnails,
 * CES badge, and an in-page detail modal on click.
 *
 * Modal shows: full-size image, performance breakdown, rule connections that
 * this exemplar supports, designation metadata, and a link to the raw asset.
 */

import { useState } from 'react';
import {
  Star,
  ExternalLink,
  Image as ImageIcon,
  X,
  Calendar,
  User,
  BarChart2,
  BookOpen,
  Link2,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  type CreativeMemoryExemplar,
  type CreativeMemoryRule,
} from '@/services/api';

interface ExemplarGalleryProps {
  exemplars: CreativeMemoryExemplar[];
  rules: CreativeMemoryRule[];
}

export function ExemplarGallery({ exemplars, rules }: ExemplarGalleryProps) {
  const [selected, setSelected] = useState<CreativeMemoryExemplar | null>(null);

  if (exemplars.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-xs text-slate-500">
        No exemplars yet — the next garden cycle will designate the
        highest-performing creatives.
      </div>
    );
  }

  const ruleLookup = new Map(rules.map((r) => [r.rule_id, r]));

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {exemplars.map((e) => (
          <ExemplarCard
            key={e.exemplar_id}
            exemplar={e}
            ruleLookup={ruleLookup}
            onClick={() => setSelected(e)}
          />
        ))}
      </div>

      {selected && (
        <ExemplarModal
          exemplar={selected}
          ruleLookup={ruleLookup}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface ExemplarCardProps {
  exemplar: CreativeMemoryExemplar;
  ruleLookup: Map<string, CreativeMemoryRule>;
  onClick: () => void;
}

function ExemplarCard({ exemplar, ruleLookup, onClick }: ExemplarCardProps) {
  const [imgError, setImgError] = useState(false);
  const ces =
    exemplar.performance_summary?.overall_score ??
    exemplar.performance_summary?.ces ??
    null;
  const demonstratesRules = exemplar.demonstrates_rule_ids
    .map((rid) => ruleLookup.get(rid))
    .filter(Boolean) as CreativeMemoryRule[];
  const hasUrl = !!exemplar.asset_url;

  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left overflow-hidden rounded-lg border border-slate-200 bg-white transition hover:border-brand-teal hover:shadow-soft cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
    >
      {/* Thumbnail */}
      <div className="relative flex h-40 items-center justify-center bg-slate-100 text-slate-300 overflow-hidden">
        {hasUrl && !imgError ? (
          <img
            src={exemplar.asset_url!}
            alt={exemplar.creative_id}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <ImageIcon className="w-8 h-8" />
        )}

        {/* CES badge */}
        {ces !== null && (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white shadow-soft">
            <Star className="w-3 h-3 fill-current" />
            CES {ces.toFixed(2)}
          </span>
        )}

        {/* Rule connection count badge */}
        {demonstratesRules.length > 0 && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-brand-teal/90 px-2 py-0.5 text-[10px] font-semibold text-white">
            <BookOpen className="w-3 h-3" />
            {demonstratesRules.length} rule{demonstratesRules.length > 1 ? 's' : ''}
          </span>
        )}

        {/* "View details" hint on hover */}
        <div className="absolute inset-0 flex items-end justify-end p-2 opacity-0 hover:opacity-100 transition-opacity bg-gradient-to-t from-black/30 to-transparent">
          <span className="text-[10px] text-white font-medium">View details →</span>
        </div>
      </div>

      {/* Meta */}
      <div className="p-3">
        <p className="text-xs font-mono text-slate-500 truncate">
          {exemplar.creative_id}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-500 truncate">
          persona: {exemplar.persona_id || '—'}
        </p>
        {exemplar.rationale && (
          <p className="mt-1.5 text-[11px] text-slate-700 leading-snug line-clamp-2">
            {exemplar.rationale}
          </p>
        )}
        {demonstratesRules.length > 0 && (
          <p className="mt-1.5 text-[10px] text-slate-500 truncate">
            <span className="font-medium uppercase tracking-wide">Demonstrates:</span>{' '}
            {demonstratesRules.map((r) => r.name).join(', ')}
          </p>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface ExemplarModalProps {
  exemplar: CreativeMemoryExemplar;
  ruleLookup: Map<string, CreativeMemoryRule>;
  onClose: () => void;
}

function ExemplarModal({ exemplar, ruleLookup, onClose }: ExemplarModalProps) {
  const [imgError, setImgError] = useState(false);
  const ces =
    exemplar.performance_summary?.overall_score ??
    exemplar.performance_summary?.ces ??
    null;
  const hookScore = exemplar.performance_summary?.hook_score ?? null;
  const personaAlignment = exemplar.performance_summary?.persona_alignment ?? null;

  const demonstratesRules = exemplar.demonstrates_rule_ids
    .map((rid) => ruleLookup.get(rid))
    .filter(Boolean) as CreativeMemoryRule[];

  const designatedAt = exemplar.designated_at
    ? new Date(exemplar.designated_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      {/* Panel */}
      <div
        className="relative flex w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1 text-slate-500 hover:text-slate-800 shadow"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Left — image */}
        <div className="flex-shrink-0 w-full md:w-80 bg-slate-100 flex items-center justify-center overflow-hidden min-h-48 md:min-h-0 md:rounded-l-xl">
          {exemplar.asset_url && !imgError ? (
            <img
              src={exemplar.asset_url}
              alt={exemplar.creative_id}
              className="h-full w-full object-contain max-h-80 md:max-h-full"
              onError={() => setImgError(true)}
            />
          ) : (
            <ImageIcon className="w-12 h-12 text-slate-300" />
          )}
        </div>

        {/* Right — details */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Header */}
          <div>
            <div className="flex items-start justify-between gap-2">
              <code className="text-sm font-mono text-slate-700 break-all">
                {exemplar.creative_id}
              </code>
              {exemplar.asset_url && (
                <a
                  href={exemplar.asset_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 flex items-center gap-1 text-[11px] text-brand-teal hover:underline"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open asset
                </a>
              )}
            </div>
            {exemplar.rationale && (
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                {exemplar.rationale}
              </p>
            )}
          </div>

          {/* Performance scores */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
              <BarChart2 className="w-3.5 h-3.5" /> Performance
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <ScoreTile label="CES" value={ces} color="emerald" />
              <ScoreTile label="Hook" value={hookScore} color="sky" />
              <ScoreTile label="Alignment" value={personaAlignment} color="violet" />
            </div>
          </section>

          {/* Rule connections */}
          {demonstratesRules.length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" /> Rules this exemplar supports
              </h3>
              <ul className="space-y-2">
                {demonstratesRules.map((rule) => (
                  <li
                    key={rule.rule_id}
                    className={clsx(
                      'rounded-md border px-3 py-2 text-xs',
                      rule.polarity === 'DO'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-rose-200 bg-rose-50 text-rose-800',
                    )}
                  >
                    <span
                      className={clsx(
                        'mr-1.5 rounded px-1 py-0.5 text-[10px] font-bold',
                        rule.polarity === 'DO'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-rose-500 text-white',
                      )}
                    >
                      {rule.polarity}
                    </span>
                    <span className="font-medium">{rule.name}</span>
                    {rule.summary && (
                      <p className="mt-1 text-[11px] opacity-80 leading-snug">{rule.summary}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Metadata */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
              <User className="w-3.5 h-3.5" /> Context
            </h3>
            <dl className="space-y-1.5 text-xs text-slate-600">
              <MetaRow
                label="Persona"
                value={exemplar.persona_id || '—'}
                mono
              />
              {exemplar.brief_id && (
                <MetaRow label="Brief" value={exemplar.brief_id} mono />
              )}
              <MetaRow
                label="Asset type"
                value={exemplar.asset_type}
              />
              <MetaRow
                label="Designated by"
                value={exemplar.designated_by}
              />
              {designatedAt && (
                <MetaRow
                  label="Designated at"
                  value={designatedAt}
                  icon={<Calendar className="w-3 h-3 text-slate-400" />}
                />
              )}
            </dl>
          </section>

          {/* Creative links */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
              <Link2 className="w-3.5 h-3.5" /> Associated creative
            </h3>
            <div className="flex flex-col gap-2">
              {/* Always available: link to the asset detail page */}
              <a
                href={`/creative/assets/${exemplar.creative_id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-teal/40 bg-brand-teal/5 px-3 py-1.5 text-xs text-brand-teal hover:bg-brand-teal/10 transition"
              >
                <BookOpen className="w-3 h-3" />
                View asset {exemplar.creative_id}
              </a>
              {/* Brief link — only shown when brief_id looks like a real brief (not a smoke artifact or pack id) */}
              {exemplar.brief_id && exemplar.brief_id.startsWith('brief_') && (
                <a
                  href={`/creative/briefs/${exemplar.brief_id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 transition"
                >
                  <BookOpen className="w-3 h-3" />
                  View brief {exemplar.brief_id}
                </a>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ScoreTile({
  label,
  value,
  color,
}: {
  label: string;
  value: number | null;
  color: 'emerald' | 'sky' | 'violet';
}) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <div className={clsx('rounded-lg border p-2 text-center', colorMap[color])}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-bold mt-0.5">
        {value !== null ? value.toFixed(2) : '—'}
      </p>
    </div>
  );
}

function MetaRow({
  label,
  value,
  mono = false,
  icon,
}: {
  label: string;
  value: string;
  mono?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      {icon}
      <span className="w-24 shrink-0 text-slate-400">{label}</span>
      <span className={clsx('break-all', mono && 'font-mono')}>{value}</span>
    </div>
  );
}
